import { isApiError, unwrap } from "@sagot/api-client";
import { WEBHOOK_SIGNATURE_HEADER } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, EmptyState, Icon, Skeleton, toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceId } from "../../../../session/workspace-context";
import { wsKeys, type WebhookOut } from "../../shared";
import { useInvalidate, useWebhooks } from "../use-settings";
import { ConnectorAuthSection } from "./connector-auth-card";
import { supportsConnectorAuth } from "./connector-auth-model";
import { DeliveriesCard } from "./deliveries-card";
import { deliveryPaused, EventDeliveryNotice, EventsPausedBadge, receivesEvents } from "./event-delivery-notice";
import { Gate, PanelError, PanelForbidden, useCanManage } from "./panel-kit";
import { RotateSecretDialog, WebhookDialog } from "./webhook-dialog";

function HealthBadge({ webhook }: { webhook: WebhookOut }) {
  const { t } = useTranslation();
  if (!webhook.enabled || webhook.disabled_at) {
    return (
      <Badge>
        <Icon name="pause" className="size-3" />
        {t("settings.webhooks.disabled")}
      </Badge>
    );
  }
  if (webhook.health.failures_24h > 0) {
    return (
      <Badge tone="warning" dot>
        {t("settings.webhooks.failures", { count: webhook.health.failures_24h })}
      </Badge>
    );
  }
  return (
    <Badge tone="success" dot>
      {t("settings.webhooks.healthy")}
    </Badge>
  );
}

type Dialog = { kind: "edit"; webhook: WebhookOut | null } | { kind: "rotate"; webhook: WebhookOut } | null;

function EndpointRow({ webhook, onDialog }: { webhook: WebhookOut; onDialog: (dialog: Dialog) => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const wsId = useWorkspaceId();
  const message = useErrorMessage();
  const invalidate = useInvalidate();
  const manage = useCanManage("webhooks.manage");
  const test = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/webhooks/{webhook_id}/test", { params: { path: { webhook_id: webhook.id } } })),
    onSuccess: async (result) => {
      const detail = { code: result.status_code ?? "—", ms: result.duration_ms ?? "—" };
      if (result.status === "succeeded") toast.success(t("settings.webhooks.testOk", detail));
      else toast.error(t("settings.webhooks.testFailed", detail), { description: result.response_excerpt ? t("settings.webhooks.testReceiver", { excerpt: result.response_excerpt }) : undefined });
      await invalidate(wsKeys.deliveries(wsId, webhook.id));
    },
    onError: (caught) => toast.error(message(caught)),
  });
  const remove = useMutation({
    mutationFn: () => unwrap(api.DELETE("/v1/webhooks/{webhook_id}", { params: { path: { webhook_id: webhook.id } } })),
    onSuccess: async () => {
      toast.success(t("settings.webhooks.deleted"));
      await invalidate(wsKeys.webhooks(wsId));
    },
    onError: (caught) => toast.error(message(caught)),
  });
  const locked = !manage.allowed;
  return (
    <li className="px-5 py-4" data-testid="webhook-endpoint">
      <div className="flex flex-wrap items-center gap-2">
        <span className="break-all font-mono text-sm font-semibold">{webhook.url}</span>
        <Badge tone="neutral" data-testid="webhook-format">
          {t(`settings.webhooks.formats.${webhook.format}`)}
        </Badge>
        <HealthBadge webhook={webhook} />
        {deliveryPaused(webhook.event_delivery) && receivesEvents(webhook.delivery_mode) ? <EventsPausedBadge /> : null}
        <div className="ml-auto flex flex-wrap gap-2">
          <Gate reason={manage.reason}>
            <Button variant="secondary" size="sm" disabled={locked} loading={test.isPending} onClick={() => { test.mutate(); }}>
              <Icon name="send" />
              {t("settings.webhooks.sendTest")}
            </Button>
          </Gate>
          <Button variant="ghost" size="sm" disabled={locked} onClick={() => { onDialog({ kind: "edit", webhook }); }} aria-label={t("settings.webhooks.editOne", { url: webhook.url })}>
            <Icon name="pencil" />
            {t("settings.webhooks.edit")}
          </Button>
          <Button variant="ghost" size="sm" disabled={locked} onClick={() => { onDialog({ kind: "rotate", webhook }); }}>
            <Icon name="rotate-ccw" />
            {t("settings.webhooks.rotate")}
          </Button>
          <Button variant="ghost" size="sm" iconOnly className="text-danger" disabled={locked || remove.isPending} aria-label={t("settings.webhooks.deleteOne", { url: webhook.url })} onClick={() => { remove.mutate(); }}>
            <Icon name="trash" />
          </Button>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {webhook.events.map((event) => (
          <Badge key={event} tone="brand" className="font-mono">
            {event}
          </Badge>
        ))}
      </div>
      {supportsConnectorAuth(webhook.delivery_mode) ? <ConnectorAuthSection webhook={webhook} /> : null}
    </li>
  );
}

/** Webhooks panel: endpoints, signing secrets (per endpoint), recent deliveries. */
export function WebhooksPanel() {
  const { t } = useTranslation();
  const wsId = useWorkspaceId();
  const invalidate = useInvalidate();
  const hooks = useWebhooks();
  const manage = useCanManage("webhooks.manage");
  const [dialog, setDialog] = useState<Dialog>(null);
  // Every endpoint reports the same Developer webhooks workflow; one that is paused sends nothing to the endpoints that listen for events.
  const eventDelivery = hooks.data?.find((webhook) => webhook.event_delivery)?.event_delivery ?? null;
  const pausedFor = deliveryPaused(eventDelivery) && (hooks.data ?? []).some((webhook) => receivesEvents(webhook.delivery_mode)) ? eventDelivery : null;
  if (isApiError(hooks.error) && hooks.error.isForbidden) return <PanelForbidden />;
  if (hooks.isError) return <PanelError error={hooks.error} onRetry={() => void hooks.refetch()} />;
  const addButton = (
    <Gate reason={manage.reason}>
      <Button size="sm" disabled={!manage.allowed} onClick={() => { setDialog({ kind: "edit", webhook: null }); }}>
        <Icon name="plus" />
        {t("settings.webhooks.add")}
      </Button>
    </Gate>
  );
  return (
    <>
      <section className="card overflow-hidden" aria-labelledby="hooks-title">
        <div className="card-header">
          <div>
            <h2 id="hooks-title" className="card-title">
              {t("settings.webhooks.title")}
            </h2>
            <p className="mt-0.5 text-xs text-fg-subtle">{t("settings.webhooks.subtitle")}</p>
          </div>
          {addButton}
        </div>
        {pausedFor ? <div className="px-5 pt-4"><EventDeliveryNotice delivery={pausedFor} /></div> : null}
        {hooks.isPending ? (
          <div className="space-y-3 p-5" aria-hidden="true">
            <Skeleton className="h-5 w-[360px] max-w-full" />
            <Skeleton className="h-5 w-[240px]" />
          </div>
        ) : hooks.data.length === 0 ? (
          <EmptyState icon="webhook" headingLevel={3} title={t("settings.webhooks.emptyTitle")} description={t("settings.webhooks.emptyBody")} action={addButton} />
        ) : (
          <ul className="divide-y divide-line">
            {hooks.data.map((webhook) => (
              <EndpointRow key={webhook.id} webhook={webhook} onDialog={setDialog} />
            ))}
          </ul>
        )}
      </section>
      <section className="card card-body" aria-labelledby="signing-title">
        <h2 id="signing-title" className="card-title">
          {t("settings.webhooks.signingTitle")}
        </h2>
        <p className="mt-1 text-xs text-fg-muted">
          {t("settings.webhooks.signingBodyLead")} <span className="font-mono">{WEBHOOK_SIGNATURE_HEADER}</span> {t("settings.webhooks.signingBodyTail")}
        </p>
      </section>
      {hooks.data && hooks.data.length > 0 ? <DeliveriesCard webhooks={hooks.data} /> : null}
      {dialog?.kind === "edit" ? <WebhookDialog webhook={dialog.webhook} eventDelivery={eventDelivery} onClose={() => { setDialog(null); }} onSaved={() => invalidate(wsKeys.webhooks(wsId))} /> : null}
      {dialog?.kind === "rotate" ? <RotateSecretDialog webhook={dialog.webhook} onClose={() => { setDialog(null); }} /> : null}
    </>
  );
}
